import React, { useContext, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ConfigContext } from '../Context/ConfigContext'
import axios from 'axios'
import { DateFormaterMonth } from '../Components/GlobalFunctions'
import ProgressPieChart from '../Components/ProgressPieChart'
import JSZip from 'jszip'
import Swal from 'sweetalert2'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

// Make sure to import autoTable correctly
import autoTable from 'jspdf-autotable'

const getRequestStatus = (status) => {
    switch (status) {
        case 0: return { text: 'Pending', color: 'warning' };
        case 1: return { text: 'Approved', color: 'success' };
        case 2: return { text: 'Rejected', color: 'danger' };
        case 3: return { text: 'Revised', color: 'info' };
        default: return { text: 'Unknown', color: 'secondary' };
    }
};

// Currency formatter for QAR
const formatCurrency = (value) =>
    new Intl.NumberFormat('en-QA', { style: 'currency', currency: 'QAR' }).format(value);

const StatusBadge = ({ status }) => {
    const { text, color } = getRequestStatus(status)
    return <span className={`badge bg-${color}`}>{text}</span>
}

const PreviewProject = () => {
    const { project_id } = useParams()
    const { apiHeaderJson, apiURL } = useContext(ConfigContext)
    const headers = apiHeaderJson

    const [project, setProject] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [downloading, setDownloading] = useState(false)

    const GetProjectDetails = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${apiURL}api/project/information`, {
                headers,
                params: { project_id }
            })

            if (response.data.success) {
                const projectData = response.data.data.project;
                const consultants = response.data.data.consultants || [];
                const contractors = response.data.data.contractors || [];
                const items = response.data.data.items || [];
                const media = response.data.data.media || [];
                const requests = response.data.data.requests || [];

                const structuredData = {
                    project_title: projectData.project_title || "Not specified",
                    project_address: projectData.project_address || "Not specified",
                    profile_name: projectData.profile_name || "Not specified",
                    profile_contact_number: projectData.profile_contact_number || "Not specified",
                    profile_email: projectData.profile_email || "Not specified",
                    budget: projectData.budget || "Not specified",
                    project_start_date: projectData.project_start_date || "",
                    project_end_date: projectData.project_end_date || "",
                    project_description: projectData.project_description || "",
                    project_status: projectData.project_status || 0,

                    contractors,
                    consultants,
                    items,
                    media,
                    requests
                };

                setProject(structuredData)
            } else {
                setError('Failed to fetch project details')
            }
        } catch (error) {
            console.error('Error fetching project details:', error)
            setError('Error fetching project details')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        GetProjectDetails()
    }, [project_id])

    // Function to create Project Information PDF
    const createProjectInfoPDF = async () => {
        const doc = new jsPDF();

        // Header
        doc.setFontSize(24);
        doc.setTextColor(26, 78, 90);
        doc.text("Project Completion Report", 20, 20);

        doc.setFontSize(16);
        doc.text(project.project_title || "", 20, 32);

        doc.setDrawColor(26, 78, 90);
        doc.line(20, 38, 190, 38);

        let yPos = 55;

        const details = [
            { label: 'Project Name:', value: project.project_title },
            { label: 'Location:', value: project.project_address },
            { label: 'Client:', value: project.profile_name },
            { label: 'Contact:', value: project.profile_contact_number },
            { label: 'Email:', value: project.profile_email },
            { label: 'Start Date:', value: DateFormaterMonth(project.project_start_date) },
            { label: 'Completion Date:', value: DateFormaterMonth(project.project_end_date) },
        ];

        if (project.project_description) {
            details.push({
                label: 'Description:',
                value: project.project_description
            });
        }

        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);

        details.forEach(({ label, value }) => {

            doc.setFont('helvetica', 'bold');
            doc.text(label, 20, yPos);

            doc.setFont('helvetica', 'normal');

            const splitValue = doc.splitTextToSize(
                String(value || ''),
                110
            );

            doc.text(splitValue, 90, yPos);

            yPos += (splitValue.length * 8) + 6;
        });

        return doc.output('blob');
    };


    const createConsultantsPDF = async () => {
        const doc = new jsPDF();

        doc.setFontSize(22);
        doc.setTextColor(26, 78, 90);
        doc.text("Project Consultants Report", 14, 20);

        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Project: ${project.project_title}`, 14, 28);

        if (project.consultants?.length > 0) {
            autoTable(doc, {
                startY: 38,
                head: [[
                    'Consultant Name',
                    'Company',
                    'Contact',
                    'Email',
                    'Role'
                ]],
                body: project.consultants.map(c => [
                    c.profile_name || 'N/A',
                    c.profile_about || 'N/A',
                    c.profile_contact_number || 'N/A',
                    c.profile_email || 'N/A',
                    c.profile_category_name || 'N/A'
                ]),
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    overflow: 'linebreak'
                },
                headStyles: {
                    fillColor: [26, 78, 90],
                    textColor: 255,
                    fontStyle: 'bold'
                }
            });
        } else {
            doc.text('No consultants available', 14, 40);
        }

        return doc.output('blob');
    };

    // Function to create Contractors PDF
    const createContractorsPDF = async () => {
        const doc = new jsPDF();

        doc.setFontSize(22);
        doc.setTextColor(26, 78, 90);
        doc.text("Project Contractors Report", 14, 20);

        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Project: ${project.project_title}`, 14, 28);

        if (project.contractors?.length > 0) {
            autoTable(doc, {
                startY: 38,
                head: [[
                    'Contractor Name',
                    'Company',
                    'Contact',
                    'Email',
                    'Role'
                ]],
                body: project.contractors.map(c => [
                    c.profile_name || 'N/A',
                    c.profile_about || 'N/A',
                    c.profile_contact_number || 'N/A',
                    c.profile_email || 'N/A',
                    c.profile_category_name || 'N/A'
                ]),
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    overflow: 'linebreak'
                },
                headStyles: {
                    fillColor: [26, 78, 90],
                    textColor: 255,
                    fontStyle: 'bold'
                }
            });
        } else {
            doc.text('No contractors available', 14, 40);
        }

        return doc.output('blob');
    };

    // Function to create Project Items PDF
    const createItemsPDF = async () => {
        const doc = new jsPDF();

        doc.setFontSize(22);
        doc.setTextColor(26, 78, 90);
        doc.text("Project Items Report", 14, 20);

        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Project: ${project.project_title}`, 14, 28);

        const grandTotal = project.items.reduce(
            (sum, item) =>
                sum +
                (Number(item.pi_item_qty || 0) *
                    Number(item.pi_item_price || 0)),
            0
        );

        if (project.items?.length > 0) {
            autoTable(doc, {
                startY: 38,
                head: [[
                    'Item Name',
                    'Description',
                    'Qty',
                    'Unit Price',
                    'Total'
                ]],
                body: project.items.map(item => {
                    const qty = Number(item.pi_item_qty || 0);
                    const price = Number(item.pi_item_price || 0);

                    return [
                        item.item_name || 'N/A',
                        item.item_description || 'N/A',
                        qty,
                        formatCurrency(price),
                        formatCurrency(qty * price)
                    ];
                }),
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    overflow: 'linebreak'
                },
                headStyles: {
                    fillColor: [26, 78, 90],
                    textColor: 255,
                    fontStyle: 'bold'
                }
            });

            const finalY = doc.lastAutoTable.finalY + 15;

            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');

            doc.text(
                `Grand Total : ${formatCurrency(grandTotal)}`,
                14,
                finalY
            );
        } else {
            doc.text('No items available', 14, 40);
        }

        return doc.output('blob');
    };

    // Function to create Submittals PDF
    const createSubmittalsPDF = async () => {
        const doc = new jsPDF();
        let yPos = 20;

        doc.setFontSize(22);
        doc.setTextColor(26, 78, 90);
        doc.text("Project Submittals Report", 14, yPos);

        yPos += 15;

        if (!project.requests?.length) {
            doc.text("No submittals available", 14, yPos);
            return doc.output("blob");
        }

        for (const request of project.requests) {

            if (yPos > 220) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFillColor(248, 249, 250);
            doc.roundedRect(10, yPos - 5, 190, 55, 3, 3, "F");

            doc.setDrawColor(220);
            doc.roundedRect(10, yPos - 5, 190, 55, 3, 3);

            doc.setFontSize(12);
            doc.setFont(undefined, "bold");

            doc.text(
                `Request #${request.project_request_id}`,
                15,
                yPos + 3
            );

            doc.setFont(undefined, "normal");

            yPos += 12;

            doc.text(
                `Status : ${getRequestStatus(request.request_status).text}`,
                15,
                yPos
            );

            doc.text(
                `Date : ${DateFormaterMonth(request.request_date)}`,
                120,
                yPos
            );

            yPos += 8;

            doc.text(
                `Item : ${request.item_name || "N/A"}`,
                15,
                yPos
            );

            yPos += 8;

            const desc = doc.splitTextToSize(
                `${request.submittal_description || ""} ${request.request_brief_description || ""}`,
                165
            );

            doc.text("Description :", 15, yPos);
        }

        return doc.output("blob");
    };

    // Function to fetch file as blob
    const fetchFileAsBlob = async (url) => {
        try {
            console.log('Fetching:', url);
            const response = await fetch(url);
            if (response.ok) {
                const blob = await response.blob();
                console.log('Successfully fetched:', url, 'Size:', blob.size);
                return blob;
            } else {
                console.error('Failed to fetch:', url, 'Status:', response.status);
                return null;
            }
        } catch (error) {
            console.error('Error fetching file:', url, error);
            return null;
        }
    };

    // Main download function
    const handleDownloadProject = async () => {
        if (!project) return;

        setDownloading(true);
        const zip = new JSZip();

        try {
            // Create main folder with project name
            const folderName = project.project_title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
            const mainFolder = zip.folder(folderName);

            const projectInfoFolder =
                mainFolder.folder("01_Project_Information");

            const imagesFolder =
                mainFolder.folder("02_Project_Images");

            const documentsFolder =
                mainFolder.folder("03_Project_Documents");

            // Generate all PDFs
            console.log('Generating PDFs...');
            const projectInfoPDF = await createProjectInfoPDF();
            const consultantsPDF = await createConsultantsPDF();
            const contractorsPDF = await createContractorsPDF();
            const itemsPDF = await createItemsPDF();
            const submittalsPDF = await createSubmittalsPDF();

            // Add PDFs to zip
            projectInfoFolder.file(
                '01_Project_Overview.pdf',
                projectInfoPDF
            );

            projectInfoFolder.file(
                '02_Consultants.pdf',
                consultantsPDF
            );

            projectInfoFolder.file(
                '03_Contractors.pdf',
                contractorsPDF
            );

            projectInfoFolder.file(
                '04_Project_Items.pdf',
                itemsPDF
            );

            projectInfoFolder.file(
                '05_Submittals.pdf',
                submittalsPDF
            );

            console.log('PDFs added to zip');

            // Create Images folder and fetch images
            console.log('Fetching images...');
            const imageMedia = project.media.filter(m => m.pm_type === 'IMAGE');
            console.log(`Found ${imageMedia.length} images`);

            for (let i = 0; i < imageMedia.length; i++) {
                const media = imageMedia[i];
                const imageUrl = `https://tms.atctransline.in/apis/public/images/${media.pm_file_name}`;
                const imageBlob = await fetchFileAsBlob(imageUrl);
                if (imageBlob && imageBlob.size > 0) {
                    const extension = media.pm_file_name.split('.').pop();
                    const fileName = `${media.pm_title || `image_${i + 1}`}.${extension}`;
                    imagesFolder.file(fileName, imageBlob);
                    console.log(`Added image: ${fileName}`);
                } else {
                    console.log(`Failed to fetch image: ${media.pm_file_name}`);
                }
            }

            // Create Documents folder and fetch documents
            console.log('Fetching documents...');
            const documentMedia = project.media.filter(m => m.pm_type === 'DOCUMENT');
            console.log(`Found ${documentMedia.length} documents`);

            for (let i = 0; i < documentMedia.length; i++) {
                const media = documentMedia[i];
                const docUrl = `https://tms.atctransline.in/apis/public/images/${media.pm_file_name}`;
                const docBlob = await fetchFileAsBlob(docUrl);
                if (docBlob && docBlob.size > 0) {
                    const extension = media.pm_file_name.split('.').pop();
                    const fileName = `${media.pm_title || `document_${i + 1}`}.${extension}`;
                    documentsFolder.file(fileName, docBlob);
                    console.log(`Added document: ${fileName}`);
                } else {
                    console.log(`Failed to fetch document: ${media.pm_file_name}`);
                }
            }

            // Generate and download the ZIP file
            console.log('Creating ZIP file...');
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const downloadLink = document.createElement('a');
            const url = URL.createObjectURL(zipBlob);
            downloadLink.href = url;
            downloadLink.download = `${folderName}_project_data.zip`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);

            console.log('Download complete!');
            Swal.fire("Success!!", "Project Complition Report Downloaded Successfully..", "success")
        } catch (error) {
            console.error('Error generating download:', error);
            alert('Error generating download. Please check console for details.');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading project details...</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (error || !project) {
        return (
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="alert alert-danger" role="alert">
                            {error || 'Project not found'}
                        </div>
                        <Link to="/archives" className="btn btn-primary">
                            Back to Archives
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    // Filter media by type
    const imageMedia = project.media.filter(m => m.pm_type === 'IMAGE');
    const documentMedia = project.media.filter(m => m.pm_type === 'DOCUMENT');

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0">Project Completion Report</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item">
                                            <Link to="/dashboard">Dashboard</Link>
                                        </li>
                                        <li className="breadcrumb-item">
                                            <Link to="/archives">Archives</Link>
                                        </li>
                                        <li className="breadcrumb-item active">Preview Project</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Download Button */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <button
                                onClick={handleDownloadProject}
                                disabled={downloading}
                                className="btn btn-success btn-label"
                                style={{ float: 'right' }}
                            >
                                {downloading ? (
                                    <>
                                        <i className='bx bx-loader bx-spin label-icon'></i>
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <i className='bx bx-download label-icon'></i>
                                        Download Project Data
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="mb-4 border-bottom pb-2">
                                        <div className="row">
                                            <div className="col-md-8">
                                                <h3>New Line Company</h3>
                                                <p style={{ color: "#1a4e5a" }}>Building Dreams, Crafting Realities</p>
                                            </div>
                                            <div className="col-md-4">
                                                <img
                                                    src="/assets/images/new_logo.png"
                                                    alt="Company Logo"
                                                    className='img-fluid float-end w-25'
                                                />
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <h2>Project Completion Report</h2>
                                        </div>
                                    </div>

                                    {/* Project Information Section */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2 mb-3 fs-5" style={{ color: "#1a4e5a" }}>Project Information</h6>
                                        <div className="row">
                                            <div className="col-md-6 mb-2">
                                                <strong>Project Name:</strong> {project.project_title}
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <strong>Project Status:</strong> <span className="badge bg-success ms-1">Completed</span>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <strong>Location:</strong> {project.project_address}
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <strong>Client:</strong> {project.profile_name}
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <strong>Contact:</strong> {project.profile_contact_number}
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <strong>Email:</strong> {project.profile_email}
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <strong>Start Date:</strong> {DateFormaterMonth(project.project_start_date)}
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <strong>Completion Date:</strong> {DateFormaterMonth(project.project_end_date)}
                                            </div>
                                            {project.project_description && (
                                                <div className="col-12 mb-2">
                                                    <strong>Description:</strong> {project.project_description}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Project Progress Chart */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2 mb-3 fs-5" style={{ color: "#1a4e5a" }}>
                                            Project Progress
                                        </h6>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <ProgressPieChart project_id={project_id} />
                                            </div>
                                            <div className="col-md-6 d-flex flex-column justify-content-center">
                                                <h6 className="mb-3" style={{ color: "#1a4e5a" }}>
                                                    Overview
                                                </h6>

                                                <p className="mb-2">
                                                    This chart represents the overall execution status of the project
                                                    based on recorded material usage and planned quantities.
                                                </p>

                                                <ul className="mb-3 ps-3">
                                                    <li>Shows current project movement in real time</li>
                                                    <li>Helps identify whether work is on track</li>
                                                    <li>Useful for client and management reports</li>
                                                </ul>

                                                <div className="fst-italic text-muted small">
                                                    *The data is synced automatically from the project management system.
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Consultants Section */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2 mb-3 fs-5" style={{ color: "#1a4e5a" }}>Consultants</h6>
                                        {project.consultants?.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Company</th>
                                                            <th>Contact</th>
                                                            <th>Email</th>
                                                            <th>Role</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {project.consultants.map((consultant, index) => (
                                                            <tr key={index}>
                                                                <td>{consultant.profile_name || "N/A"}</td>
                                                                <td>{consultant.profile_about || "N/A"}</td>
                                                                <td>{consultant.profile_contact_number || "N/A"}</td>
                                                                <td>{consultant.profile_email || "N/A"}</td>
                                                                <td>{consultant.profile_category_name || "N/A"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p>No consultants available</p>
                                        )}
                                    </div>

                                    {/* Contractors Section */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2 mb-3 fs-5" style={{ color: "#1a4e5a" }}>Contractors</h6>
                                        {project.contractors?.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Company</th>
                                                            <th>Contact</th>
                                                            <th>Email</th>
                                                            <th>Role</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {project.contractors.map((contractor, index) => (
                                                            <tr key={index}>
                                                                <td>{contractor.profile_name || "N/A"}</td>
                                                                <td>{contractor.profile_about || "N/A"}</td>
                                                                <td>{contractor.profile_contact_number || "N/A"}</td>
                                                                <td>{contractor.profile_email || "N/A"}</td>
                                                                <td>{contractor.profile_category_name || "N/A"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p>No contractors available</p>
                                        )}
                                    </div>

                                    {/* Items Section */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2 mb-3 fs-5" style={{ color: "#1a4e5a" }}>Project Items</h6>
                                        {project.items?.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Item Name</th>
                                                            <th>Description</th>
                                                            <th>Quantity</th>
                                                            <th>Price</th>
                                                            <th>Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {project.items.map((item, index) => (
                                                            <tr key={index}>
                                                                <td>{item.item_name || "N/A"}</td>
                                                                <td>{item.item_description || "N/A"}</td>
                                                                <td>{item.pi_item_qty || "N/A"}</td>
                                                                <td>{item.pi_item_price ? formatCurrency(item.pi_item_price) : "N/A"}</td>
                                                                <td>
                                                                    {item.pi_item_qty && item.pi_item_price
                                                                        ? formatCurrency(item.pi_item_qty * item.pi_item_price)
                                                                        : "N/A"}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p>No items available</p>
                                        )}
                                    </div>

                                    {/* Submittals Section */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2 mb-3 fs-5" style={{ color: "#1a4e5a" }}>Submittals</h6>
                                        {project.requests?.length > 0 ? (
                                            project.requests.map((request, index) => (
                                                <div key={index} className="card mb-2">
                                                    <div className="card-body">
                                                        <div className="row">
                                                            <div className="col-md-6 mb-1">
                                                                <strong>Request ID:</strong> {request.project_request_id}
                                                            </div>
                                                            <div className="col-md-6 mb-1">
                                                                <strong>Status:</strong> <StatusBadge status={request.request_status} />
                                                            </div>
                                                            <div className="col-md-12 mb-1">
                                                                <strong>Description:</strong> {request.submittal_description}: {request.request_brief_description}
                                                            </div>
                                                            <div className="col-md-6 mb-1">
                                                                <strong>Item:</strong> {request.item_name}
                                                            </div>
                                                            <div className="col-md-6 mb-1">
                                                                <strong>Date:</strong> {DateFormaterMonth(request.request_date)}
                                                            </div>
                                                            <div className="col-md-6 mb-1">
                                                                <strong>Requested By:</strong> {request.requested_by || "Unknown"}
                                                            </div>
                                                            <div className="col-md-6 mb-1">
                                                                <strong>Status Updated By:</strong> {request.status_updated_by || "Unknown"}
                                                            </div>
                                                            {request.request_status === 3 && (
                                                                <div className="col-md-6 mb-1">
                                                                    <strong>Revised By:</strong> {request.revised_by || "Unknown"}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p>No submittals available</p>
                                        )}
                                    </div>

                                    {/* Documents Section */}
                                    {documentMedia.length > 0 && (
                                        <div className="mb-4">
                                            <h6 className="border-bottom pb-2 mb-3 fs-5" style={{ color: "#1a4e5a" }}>Project Documents</h6>
                                            <div className="row">
                                                {documentMedia.map((media, index) => (
                                                    <div key={index} className="col-md-4 mb-3">
                                                        <div className="card">
                                                            <div className="card-body text-center">
                                                                <i className="bx bx-file display-4 text-muted"></i>
                                                                <h6 className="card-title mt-2">{media.pm_title || "Untitled Document"}</h6>
                                                                <a
                                                                    href={`https://tms.atctransline.in/apis/public/images/${media.pm_file_name.replace(/\s+/g, '%20')}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-primary btn-sm"
                                                                >
                                                                    View Document
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Pictures Section */}
                                    <div>
                                        <h6 className="border-bottom pb-2 mb-3 fs-5" style={{ color: "#1a4e5a" }}>Project Images</h6>
                                        {imageMedia.length > 0 ? (
                                            <div className="row">
                                                {imageMedia.map((media, index) => (
                                                    <div key={index} className="col-md-4 mb-3">
                                                        <div className="card">
                                                            <img
                                                                src={`https://tms.atctransline.in/apis/public/images/${media.pm_file_name.replace(/\s+/g, '%20')}`}
                                                                className="card-img-top"
                                                                alt={media.pm_title || "Project image"}
                                                                style={{ height: '200px', objectFit: 'cover' }}
                                                            />
                                                            <div className="card-body">
                                                                <p className="card-text text-center">
                                                                    {media.pm_title || "Untitled"}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p>No images available</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <Link to="/archives" className="btn btn-secondary btn-label">
                                <i className='ri-arrow-left-line align-middl label-icon'></i> Back
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PreviewProject